import React, { useEffect, useState } from 'react';
import { Grid, Link as LinkMui, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'hooks/useTranslations';
import { useGlobalProperty } from 'models/globalPropertyContext';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { ERouteLinks } from 'models/route';
import { Loader } from 'components/atoms/Loader';
import { Chips } from 'components/atoms/Chips';
import { CardList, emptyFieldHolder } from 'components/molecules/CardList';
import { formatDate } from 'utils/date';
import { StyledValueDetails } from 'pages/LicenceAuthoritiesPage/styles';
import { useLicences } from '../hooks';
import {
  licencePrintStatusVariant,
  mapPrintHistoryToDataSource,
  printoutsHistoryTableColumns,
} from '../helpers';
import { SftpAlertBanner } from '../SftpAlertBanner/SftpAlertBanner';
import { mapToLegalType } from 'constants/LegalTypes';
import { ELicencePrintStatus } from 'constants/LicencePrintStatus';
import { useLang } from 'models/langContext';
import { findInDictionary } from 'helpers/dictionary';
import { Accordion } from 'components/molecules/Accordion';
import { Table } from 'components/molecules/Table';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { PrintoutsHistoryTableCard } from './PrintoutsHistoryTableCard';

export const PrintoutDetails = () => {
  const { t } = useTranslations();
  const { id = '' } = useParams<{ id: string }>();
  const { longDateFormat, getProperties } = useGlobalProperty();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const { printoutDetails, getPrintoutDetailsData } = useLicences();
  const { selectedLanguage } = useLang();
  const { isDesktop } = useThemeBreakpoint();
  const {
    weaponLicencePrintStatus,
    getWeaponLicencePrintStatus,
    getWeaponLicencingAuthority,
    weaponLicencingAuthority,
    getWeaponLicencePrintHistoryData,
    printHistory,
  } = useLicences();

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    getWeaponLicencePrintStatus();
    getWeaponLicencingAuthority();
  }, [selectedLanguage]);

  useEffect(() => {
    (async function init() {
      setIsLoading(true);
      await getPrintoutDetailsData(id);
      setIsLoading(false);
    })();
  }, [id]);
  useEffect(() => {
    (async function init() {
      if (id) {
        setIsLoadingHistory(true);
        await getWeaponLicencePrintHistoryData(id);
        setIsLoadingHistory(false);
      }
    })();
  }, [id]);

  const licenceAuthorityUniqueId = printoutDetails?.licenceAuthorityUniqueId;

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.licences.name'), route: ERouteLinks.LicencesPage },
          {
            label: t('module.printouts.name'),
            route: `${ERouteLinks.LicencesPage}?expand=printouts`,
          },
          t('licences.printoutDetails.header'),
        ]}
      />
      <StyledPageTitle variant="h4" sx={{ mb: 2 }}>
        {t('licences.printoutDetails.header')}
      </StyledPageTitle>

      {printoutDetails &&
        ![
          ELicencePrintStatus.MISSING,
          ELicencePrintStatus.PRINTED,
          ELicencePrintStatus.FAILED,
          ELicencePrintStatus.CANCELLED,
          ELicencePrintStatus.FAILED_TO_SEND,
        ].includes(printoutDetails?.status as ELicencePrintStatus) && (
          <SftpAlertBanner licenceAuthorityUniqueId={licenceAuthorityUniqueId} />
        )}

      {(isLoading || isLoadingHistory) && <Loader isLoading />}

      {!isLoading && (
        <StyledValueDetails container size={{ xs: 12 }} columnSpacing="10px">
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licences.printoutId.label')}
            </Typography>
            <Typography variant="body2">{emptyFieldHolder(printoutDetails?.id)}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licences.licenceNumber.label')}
            </Typography>
            {printoutDetails?.licenceUniqueId ? (
              <LinkMui
                href={ERouteLinks.DetailLicence.replace(
                  ':licenceId',
                  printoutDetails.licenceUniqueId,
                )}
                underline="none"
                target="_blank"
                rel="noopener noreferrer"
              >
                {printoutDetails.licenceNumber}
              </LinkMui>
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licences.licenceOwnerName.label')}
            </Typography>
            {printoutDetails?.licenceOwnerUniqueId ? (
              <LinkMui
                href={`${ERouteLinks.ViewLegalEntity.replace(
                  ':id',
                  printoutDetails.licenceOwnerUniqueId,
                )}?type=${mapToLegalType(printoutDetails.licenceOwnerTypeUniqueId)}`}
                underline="none"
                target="_blank"
                rel="noopener noreferrer"
              >
                {printoutDetails.licenceOwner}
              </LinkMui>
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licences.status.label')}
            </Typography>
            {printoutDetails?.statusUniqueId ? (
              <Chips
                text={findInDictionary(
                  printoutDetails.statusUniqueId,
                  weaponLicencePrintStatus,
                )}
                fullWidth
                variant={licencePrintStatusVariant(printoutDetails.statusUniqueId)}
              />
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licences.licenceAuthority.label')}
            </Typography>
            <Typography variant="body2">
              {emptyFieldHolder(
                findInDictionary(
                  printoutDetails?.licenceAuthorityUniqueId,
                  weaponLicencingAuthority,
                ),
              )}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licences.createDate.label')}
            </Typography>
            <Typography variant="body2">
              {emptyFieldHolder(formatDate(printoutDetails?.createdDate, longDateFormat))}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licences.printDate.label')}
            </Typography>
            <Typography variant="body2">
              {emptyFieldHolder(formatDate(printoutDetails?.printDate, longDateFormat))}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licences.batchName.label')}
            </Typography>
            {printoutDetails?.batchUniqueId ? (
              <LinkMui
                href={ERouteLinks.PrintoutBatchDetails.replace(
                  ':id',
                  printoutDetails.batchUniqueId,
                )}
                underline="none"
                target="_blank"
                rel="noopener noreferrer"
              >
                {printoutDetails.batchName}
              </LinkMui>
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </Grid>
        </StyledValueDetails>
      )}
      {!isLoadingHistory && (
        <Accordion
          title={t('legalEntity.licence.printoutHistory.label')}
          name="printoutHistory"
          key="printoutHistory"
          expanded
          alwaysExpanded
          hideExpandIcon
        >
          {isDesktop ? (
            <Table
              columns={printoutsHistoryTableColumns({ t })}
              dataSource={mapPrintHistoryToDataSource(printHistory, longDateFormat)}
            />
          ) : (
            <CardList
              items={mapPrintHistoryToDataSource(printHistory, longDateFormat)}
              render={(data, index) => <PrintoutsHistoryTableCard data={data} key={index} />}
            />
          )}
        </Accordion>
      )}
    </>
  );
};
